/**
 * Scroll Depth Tracking
 * =====================
 * Sends scroll_depth events to the analytics pipeline (not CRM).
 */

import { trackScrollDepth } from '@/lib/analytics';
import { dlScrollDepth, dlArticleScroll50, dlArticleScroll90 } from '@/lib/dataLayer';
import { shouldTrackCommercialRoute } from '@/hooks/usePageTracking';

const SCROLL_MARKERS = [25, 50, 75, 90, 100] as const;
const THROTTLE_MS = 250;

let lastScrollTime = 0;
let scrollHandler: (() => void) | null = null;
let currentRoute: string | null = null;

function getSessionKey(route: string, marker: number): string {
  return `crm_scroll_${route}_${marker}`;
}

function hasMarkerFired(route: string, marker: number): boolean {
  if (typeof window === 'undefined') return true;
  return sessionStorage.getItem(getSessionKey(route, marker)) === '1';
}

function markMarkerFired(route: string, marker: number): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(getSessionKey(route, marker), '1');
}

function inferContentType(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/produto/')) return 'pdp';
  if (pathname.startsWith('/blog/')) return 'blog';
  return 'page';
}

function inferPageGroup(pathname: string): string {
  if (pathname === '/') return 'home';
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || 'home';
}

function getLayoutMetrics(): { docHeight: number; viewportHeight: number; scrollY: number } {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { docHeight: 0, viewportHeight: 0, scrollY: 0 };
  }
  return {
    docHeight: document.documentElement.scrollHeight,
    viewportHeight: window.innerHeight,
    scrollY: window.scrollY,
  };
}

function calculateScrollDepth(): number {
  const { docHeight, viewportHeight, scrollY } = getLayoutMetrics();
  if (docHeight <= viewportHeight) return 100; // Page fits in viewport
  const scrolled = scrollY + viewportHeight;
  return Math.min(100, Math.round((scrolled / docHeight) * 100));
}

function handleScroll(): void {
  if (typeof window === 'undefined') return;

  const route = window.location.pathname;

  // Skip internal routes
  if (!shouldTrackCommercialRoute(route)) return;
  
  const now = Date.now();
  if (now - lastScrollTime < THROTTLE_MS) return;
  lastScrollTime = now;
  const depthPct = calculateScrollDepth();
  const { docHeight, viewportHeight, scrollY } = getLayoutMetrics();
  
  for (const marker of SCROLL_MARKERS) {
    if (depthPct >= marker && !hasMarkerFired(route, marker)) {
      markMarkerFired(route, marker);
      
      // Send to analytics pipeline (separate from CRM)
      trackScrollDepth(marker, route);
      // GTM dataLayer
      dlScrollDepth(marker, route);
      // Article engagement milestones
      if (marker === 50) dlArticleScroll50(document.title, route);
      if (marker === 90) dlArticleScroll90(document.title, route);
    }
  }
}

export function initScrollDepthTracking(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  
  // Clean up previous handler if exists
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
  }
  
  currentRoute = window.location.pathname;
  scrollHandler = handleScroll;
  
  window.addEventListener('scroll', scrollHandler, { passive: true });
  
  // Check initial scroll position (for pages already scrolled or short pages)
  setTimeout(() => handleScroll(), 100);
  
  // Return cleanup function
  return () => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
  };
}

// Reset tracking when route changes (call this from router)
export function resetScrollDepthForRoute(): void {
  if (typeof window === 'undefined') return;
  
  const newRoute = window.location.pathname;
  if (newRoute !== currentRoute) {
    currentRoute = newRoute;
    // No need to clear sessionStorage - keys are route-specific
    // Just update current route reference
  }
}
